import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { getUserHistory, ChatSession } from '../services/historyService';
import { listBaziSessions, getBaziSession, deleteBaziSession, stashBaziRestoreSession, BaziSessionSummary, BaziSessionFull, BaziChatMessage } from '../services/baziAiService';
import { HexagramSummary } from '../components/HexagramDisplay';
import { TiYongBoard } from '../components/TiYongChart';
import { AiResponseDisplay } from '../components/AiResponseDisplay';
import { ChatBubble } from '../components/ChatBubble';
import { BaziAiThreadView } from '../components/BaziAiChat';
// [MHQI] 奇门分区：services + 线程视图
import { listQimenSessions, getQimenSession, deleteQimenSession, stashQimenRestoreSession, QimenSessionSummary, QimenSessionFull, QimenChatMessage } from '../services/qimenAiService';
import { QimenAiThreadView } from '../components/QimenAiChat';
import { fromHexagramDTO } from '../core/meihua';
import { PageHeader } from '../components/PageHeader';

// ============================================================
// 八字分区：会话列表 + 详情（命盘摘要 + 完整对话 + 去八字页继续）
// ============================================================
const BaziHistorySection: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<BaziSessionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<BaziSessionFull | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const fetchList = async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError('');
    try {
      setSessions(await listBaziSessions(user));
    } catch (err: any) {
      console.error(err);
      setLoadError('八字求测记录暂时读不到，稍后重试。');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const toggleExpand = async (id: string) => {
    if (expandedId === id) {
      setExpandedId(null);
      setDetail(null);
      return;
    }
    setExpandedId(id);
    setDetail(null);
    setDetailLoading(true);
    try {
      setDetail(await getBaziSession(user, id));
    } catch (err) {
      console.error(err);
    } finally {
      setDetailLoading(false);
    }
  };

  const doDelete = async (id: string) => {
    if (confirmDeleteId !== id) {
      setConfirmDeleteId(id);
      window.setTimeout(() => setConfirmDeleteId(cur => (cur === id ? null : cur)), 4000);
      return;
    }
    setConfirmDeleteId(null);
    try {
      await deleteBaziSession(user, id);
      if (expandedId === id) {
        setExpandedId(null);
        setDetail(null);
      }
      setSessions(prev => prev.filter(s => s.id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  // 恢复该盘到 /app/bazi：BaziPage 消费 bz-ai-restore 重建盘面，
  // BaziAiChat 按命盘身份（solarText|gender|name|city）命中本地缓存恢复对话
  const continueOnBaziPage = () => {
    if (detail?.chart) {
      stashBaziRestoreSession(detail);
      navigate('/app/bazi');
    }
  };

  if (loading) {
    return (
      <div className="mei-card" style={{ textAlign: 'center', padding: 40 }}>
        <div className="mei-loading-text">加载八字求测记录中...</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="mei-card" style={{ textAlign: 'center', padding: 32 }}>
        <div style={{ color: 'var(--text-secondary)', fontSize: 14 }}>{loadError}</div>
        <button className="mei-btn" style={{ marginTop: 14 }} onClick={() => void fetchList()}>重试</button>
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div className="mei-card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 20px', gap: 8 }}>
        <div style={{ textAlign: 'center' }}>
          <div className="mei-section-title" style={{ fontSize: 18, marginBottom: 8 }}>暂无八字求测</div>
          <div className="mei-section-subtitle">
            到「八字」页排盘后，用「AI 求测与解读」与大师对话；历史会话与命盘快照会保存在这里。
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {sessions.map(session => {
        const isExpanded = expandedId === session.id;
        const dateStr = new Date(session.updatedAt).toLocaleString('zh-CN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
        return (
          <div key={session.id} className="mei-card" style={{ transition: 'all .3s ease' }}>
            <div onClick={() => void toggleExpand(session.id)} style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', minWidth: 0 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4, overflowWrap: 'anywhere' }}>
                  {session.title}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {session.solarText.slice(0, 16)}{session.gender ? ` · ${session.gender}` : ''}{session.name ? ` · ${session.name}` : ''} · {session.messagesCount} 条 · {dateStr}
                </div>
              </div>
              <div style={{ color: 'var(--text-secondary)', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform .3s' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
              </div>
            </div>

            {isExpanded && (
              <div style={{ marginTop: 16, borderTop: '1px solid var(--border-color)', paddingTop: 16 }}>
                {detailLoading && <div className="mei-loading-text" style={{ textAlign: 'center' }}>载入会话中...</div>}
                {detail && (
                  <>
                    <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', background: 'var(--bg-secondary)', borderRadius: 10, padding: '8px 12px', marginBottom: 12 }}>
                      命盘快照：{detail.chart?.solarText || '—'}{detail.chart?.input?.city && detail.chart.input.city !== '不校正' ? `（${detail.chart.input.city}）` : ''}
                      {detail.chart?.dayGan ? ` · 日主 ${detail.chart.dayGan}` : ''}
                      {detail.chart?.strength?.level ? ` · ${detail.chart.strength.level}` : ''}
                      {detail.chart?.strength?.favor?.length ? ` · 喜用 ${detail.chart.strength.favor.join('、')}` : ''}
                      {detail.chart?.missing?.length ? ` · 缺 ${detail.chart.missing.join('、')}` : ''}
                    </div>
                    <div className="mh-scope" style={{ margin: '0 -4px' }}>
                      <BaziAiThreadView messages={(detail.messages || []) as BaziChatMessage[]} />
                    </div>
                    <div style={{ marginTop: 14, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <button className="mei-btn" onClick={continueOnBaziPage}>去八字页继续追问</button>
                      <button className="mei-btn" onClick={() => void doDelete(session.id)}>
                        {confirmDeleteId === session.id ? '再点一次确认删除' : '删除记录'}
                      </button>
                    </div>
                  </>
                )}
                {!detailLoading && !detail && (
                  <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>会话不存在或已被删除。</div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ============================================================
// 奇门分区：会话列表 + 详情（局象快照 + 完整对话 + 去奇门页继续）
// ============================================================
const QimenHistorySection: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<QimenSessionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<QimenSessionFull | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const fetchList = async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError('');
    try {
      setSessions(await listQimenSessions(user));
    } catch (err: any) {
      console.error(err);
      setLoadError('奇门求测记录暂时读不到，稍后重试。');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const toggleExpand = async (id: string) => {
    if (expandedId === id) {
      setExpandedId(null);
      setDetail(null);
      return;
    }
    setExpandedId(id);
    setDetail(null);
    setDetailLoading(true);
    try {
      setDetail(await getQimenSession(user, id));
    } catch (err) {
      console.error(err);
    } finally {
      setDetailLoading(false);
    }
  };

  const doDelete = async (id: string) => {
    if (confirmDeleteId !== id) {
      setConfirmDeleteId(id);
      window.setTimeout(() => setConfirmDeleteId(cur => (cur === id ? null : cur)), 4000);
      return;
    }
    setConfirmDeleteId(null);
    try {
      await deleteQimenSession(user, id);
      if (expandedId === id) {
        setExpandedId(null);
        setDetail(null);
      }
      setSessions(prev => prev.filter(s => s.id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  // 恢复此局到 /app/qimen：QimenPage 消费 qm-ai-restore 重建排盘（含盘法），
  // QimenAiChat 按 局身份（solarText|shenMode|juMethod）命中本地缓存恢复对话
  const continueOnQimenPage = () => {
    if (detail?.chart) {
      stashQimenRestoreSession(detail);
      navigate('/app/qimen');
    }
  };

  if (loading) {
    return (
      <div className="mei-card" style={{ textAlign: 'center', padding: 40 }}>
        <div className="mei-loading-text">加载奇门求测记录中...</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="mei-card" style={{ textAlign: 'center', padding: 32 }}>
        <div style={{ color: 'var(--text-secondary)', fontSize: 14 }}>{loadError}</div>
        <button className="mei-btn" style={{ marginTop: 14 }} onClick={() => void fetchList()}>重试</button>
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div className="mei-card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 20px', gap: 8 }}>
        <div style={{ textAlign: 'center' }}>
          <div className="mei-section-title" style={{ fontSize: 18, marginBottom: 8 }}>暂无奇门求测</div>
          <div className="mei-section-subtitle">
            到「奇门」页起局后，用「AI 求测与解读」与大师对话；历史会话与此局快照会保存在这里。
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {sessions.map(session => {
        const isExpanded = expandedId === session.id;
        const dateStr = new Date(session.updatedAt).toLocaleString('zh-CN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
        return (
          <div key={session.id} className="mei-card" style={{ transition: 'all .3s ease' }}>
            <div onClick={() => void toggleExpand(session.id)} style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', minWidth: 0 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4, overflowWrap: 'anywhere' }}>
                  {session.title}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {session.solarText.slice(0, 16)}{session.yinYang ? ` · ${session.yinYang}遁` : ''}{session.ju ? ` · ${session.ju}局` : ''} · {session.messagesCount} 条 · {dateStr}
                </div>
              </div>
              <div style={{ color: 'var(--text-secondary)', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform .3s' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
              </div>
            </div>

            {isExpanded && (
              <div style={{ marginTop: 16, borderTop: '1px solid var(--border-color)', paddingTop: 16 }}>
                {detailLoading && <div className="mei-loading-text" style={{ textAlign: 'center' }}>载入会话中...</div>}
                {detail && (
                  <>
                    <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', background: 'var(--bg-secondary)', borderRadius: 10, padding: '8px 12px', marginBottom: 12 }}>
                      局快照：{detail.chart?.solarText || '—'}
                      {detail.chart?.yinYang ? ` · ${detail.chart.yinYang}遁${detail.chart.ju}局` : ''}
                      {detail.chart?.jieqi ? ` · ${detail.chart.jieqi}` : ''}
                      {detail.chart?.zhiFu ? ` · 值符${detail.chart.zhiFu}` : ''}
                      {detail.chart?.zhiShi ? ` · 值使${detail.chart.zhiShi}` : ''}
                      {detail.chart?.input?.shenMode ? ` · ${detail.chart.input.shenMode === 'nine' ? '九神盘' : detail.chart.input.shenMode === 'yin' ? '顺布' : '阳顺阴逆'}` : ''}
                    </div>
                    <div className="mh-scope" style={{ margin: '0 -4px' }}>
                      <QimenAiThreadView messages={(detail.messages || []) as QimenChatMessage[]} />
                    </div>
                    <div style={{ marginTop: 14, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <button className="mei-btn" onClick={continueOnQimenPage}>去奇门页继续追问</button>
                      <button className="mei-btn" onClick={() => void doDelete(session.id)}>
                        {confirmDeleteId === session.id ? '再点一次确认删除' : '删除记录'}
                      </button>
                    </div>
                  </>
                )}
                {!detailLoading && !detail && (
                  <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>会话不存在或已被删除。</div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ============================================================
// 以下为原有梅花历史逻辑（不变），外层新增分区切换
// ============================================================

export const HistoryPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState<'meihua' | 'bazi' | 'qimen'>('meihua');
  const [history, setHistory] = useState<ChatSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    const fetchHistory = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      try {
        const data = await getUserHistory(user.uid);
        setHistory(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [user]);

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const renderResponse = (response: any) => {
    // If response is a string, try to parse it as JSON or display as text
    if (typeof response === 'string') {
      try {
        const data = JSON.parse(response);
        if (data.introduction || data.summary) {
          return <AiResponseDisplay response={data} />;
        }
        return <ChatBubble role="assistant" content={response} />;
      } catch {
        return <ChatBubble role="assistant" content={response} />;
      }
    }

    // If response is an object (already parsed or stored as map)
    if (response && typeof response === 'object') {
       // Check if it matches AIResponse structure
       if (response.introduction || response.summary || response.beginning) {
         return <AiResponseDisplay response={response} />;
       }
       // Fallback: check for follow-up response structure
       if (response.contextAcknowledgment && response.practicalGuidance) {
          return <ChatBubble role="assistant" content={`${response.contextAcknowledgment}\n\n${response.practicalGuidance}`} />;
       }
       // Last resort: stringify
       return <ChatBubble role="assistant" content={JSON.stringify(response, null, 2)} />;
    }

    return null;
  };

  const tabs: Array<{ key: 'meihua' | 'bazi' | 'qimen'; label: string }> = [
    { key: 'meihua', label: '梅花' },
    { key: 'qimen', label: '奇门' },
    { key: 'bazi', label: '八字' }
  ];

  const goCreate = () => {
    navigate(tab === 'meihua' ? '/app/meihua' : tab === 'qimen' ? '/app/qimen' : '/app/bazi');
  };

  return (
    <div className="page">
      <PageHeader
        eyebrow="求测档案"
        title="记录"
        subtitle="每一次起卦、排盘与追问都会留在这里，按术数分类回看。"
      />

      <div className="segmented segmented-wide" role="tablist" aria-label="记录分类">
        {tabs.map(t => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            className={tab === t.key ? 'is-active' : ''}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'qimen' ? (
        <QimenHistorySection />
      ) : tab === 'bazi' ? (
        <BaziHistorySection />
      ) : loading ? (
        <div className="mei-card" style={{ textAlign: 'center', padding: 40 }}>
          <div className="mei-loading-text">加载历史记录中...</div>
        </div>
      ) : history.length === 0 ? (
        <div className="empty-state">
          <div className="empty-mark" aria-hidden>卦</div>
          <div className="empty-title">还没有梅花记录</div>
          <p className="empty-sub">第一次起卦后，问题、卦象与完整解读都会保存在这里。</p>
          <button className="mei-primary-btn empty-action" onClick={goCreate}>去起一卦</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {history.map(session => {
            const isExpanded = expandedId === session.id;
            // Timestamp handling: Firestore timestamp or Date object
            const rawDate = session.timestamp || session.createdAt || session.updatedAt;
            const dateObj = rawDate?.toDate ? rawDate.toDate() : new Date(rawDate || Date.now());
            const dateStr = dateObj.toLocaleString('zh-CN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
            const hexagramPayload = session.hexagram || session.hexagrams;

            return (
              <div key={session.id} className="mei-card" style={{ transition: 'all 0.3s ease' }}>
                <div
                  onClick={() => toggleExpand(session.id)}
                  style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4, overflowWrap: 'anywhere' }}>
                      {session.question}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      {dateStr}
                    </div>
                  </div>
                  <div style={{ color: 'var(--text-secondary)', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.3s' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </div>
                </div>

                {isExpanded && (
                  <div style={{ marginTop: 16, borderTop: '1px solid var(--border-color)', paddingTop: 16 }}>

                    {/* Hexagrams Display */}
                    {hexagramPayload && (() => {
                      try {
                        const original = hexagramPayload.original ? fromHexagramDTO(hexagramPayload.original) : undefined;
                        const mutual = hexagramPayload.mutual ? fromHexagramDTO(hexagramPayload.mutual) : undefined;
                        const changed = hexagramPayload.changed ? fromHexagramDTO(hexagramPayload.changed) : undefined;

                        const yueling = hexagramPayload.yueling || null;

                        // 三卦齐全时给出体用标注 + 生克判读；残缺的老记录退回简版卦象
                        if (original && mutual && changed) {
                          return (
                            <div style={{ marginBottom: 24 }}>
                              <TiYongBoard
                                original={original}
                                mutual={mutual}
                                changed={changed}
                                yueling={yueling}
                                size="sm"
                              />
                            </div>
                          );
                        }

                        return (
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-around',
                            marginBottom: 24,
                            backgroundColor: 'var(--bg-secondary)',
                            padding: '12px',
                            borderRadius: '12px'
                          }}>
                            {original && <HexagramSummary label="本卦" hexagram={original} />}
                            {mutual && <HexagramSummary label="互卦" hexagram={mutual} />}
                            {changed && <HexagramSummary label="变卦" hexagram={changed} />}
                          </div>
                        );
                      } catch (e) {
                        console.error("Error rendering hexagrams in history:", e);
                        return null;
                      }
                    })()}

                    {/* Conversation History */}
                    <div style={{ marginBottom: 24 }}>
                      {/* Main Question */}
                      <ChatBubble role="user" content={session.question} />

                      {/* Main Response */}
                      {renderResponse(session.response)}
                    </div>

                    {/* Follow-up Questions */}
                    {session.followUpQuestions && session.followUpQuestions.length > 0 && (
                      <div style={{ borderTop: '1px dashed var(--border-color)', paddingTop: 16, marginTop: 16 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 12 }}>追问记录</div>
                        {session.followUpQuestions.map((fq, idx) => (
                          <div key={idx} style={{ marginBottom: 24 }}>
                            <ChatBubble role="user" content={fq.question} />
                            {renderResponse(fq.response)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
